import { http } from './request'

/** 上报状态（与后台 reportStatus 一致） */
export type RepatriationReportStatus = 'SUBMITTED' | 'CONFIRMED' | 'REJECTED' | 'CANCELED'

/** 上报方式：1 移动端 / 2 管理员后台 / 3 二维码 */
export type ReportingMethodCode = 1 | 2 | 3

export interface RepatriationRecord {
  id: string
  personnelId?: string
  enterpriseId?: string
  personName: string
  idCardMasked?: string
  phoneMasked?: string
  /** 户籍地（详情/列表接口关联人员填充） */
  registeredResidence?: string
  /** 关联企业展示名（详情/列表接口填充） */
  enterpriseName?: string
  departureCountry?: string
  departureAddress?: string
  /** 户籍区县区划码（数据权限，列表一般不展示） */
  domesticAddressCode?: string
  returnTime: string
  returnTransportType?: string
  returnStayAddress?: string
  returnStayAddressCode?: string
  returnContactWay?: string
  returnFlightNo?: string
  locationLng?: string
  locationLat?: string
  reportStatus: RepatriationReportStatus
  rejectReason?: string
  auditUser?: string
  auditTime?: string
  reportingMethod?: number
  remark?: string
  createTime?: string
  updateTime?: string
}

export interface RepatriationPageParams {
  current: number
  size: number
  personKeyword?: string
  reportStatus?: RepatriationReportStatus
  reportingMethod?: number
  locationKey?: string
  returnStayAddressCode?: string
}

export interface RepatriationPageResult {
  list: RepatriationRecord[]
  total: number
}

export interface RepatriationOverview {
  monthlyReturnCount: number
  qrScanCount: number
  pendingCount: number
  /** 归国地点数（接口 returnLocationCount） */
  returnLocationCount: number
}

/** GET /biz/overseas/return/statistics 返回体 */
export interface OverseasReturnStatisticsPayload {
  monthReturnedCount?: number
  qrCodeScanCount?: number
  pendingConfirmCount?: number
  returnLocationCount?: number
}

/** GET /biz/overseas/return/page 原始分页体（Spring 风格） */
export interface OverseasReturnPageData {
  records?: unknown[]
  list?: unknown[]
  rows?: unknown[]
  total?: number
  totalCount?: number
  totalElements?: number
  size?: number
  current?: number
}

function num(v: unknown): number {
  const x = Number(v)
  return Number.isFinite(x) ? x : 0
}

function pickStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

function parseReportStatus(v: unknown): RepatriationReportStatus {
  const s = String(v ?? '')
    .trim()
    .toUpperCase()
  if (s === 'SUBMITTED' || s === 'CONFIRMED' || s === 'REJECTED' || s === 'CANCELED') return s
  return 'SUBMITTED'
}

function parseReportingMethod(v: unknown): number | undefined {
  const n = num(v)
  if (n === 1 || n === 2 || n === 3) return n
  return undefined
}

function mapReturnRecord(raw: unknown, index: number): RepatriationRecord | null {
  if (raw == null || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const id = pickStr(o, 'id')
  if (!id) return null
  const personName = pickStr(o, 'personName', 'name')
  return {
    id,
    personnelId: pickStr(o, 'personnelId'),
    enterpriseId: pickStr(o, 'enterpriseId'),
    personName: personName || `记录${index + 1}`,
    idCardMasked: pickStr(o, 'idCardMasked', 'idCard'),
    phoneMasked: pickStr(o, 'phoneMasked', 'phone'),
    registeredResidence: pickStr(o, 'registeredResidence'),
    enterpriseName: pickStr(o, 'enterpriseName'),
    departureCountry: pickStr(o, 'departureCountry'),
    departureAddress: pickStr(o, 'departureAddress'),
    domesticAddressCode: pickStr(o, 'domesticAddressCode', 'domestic_address_code'),
    returnTime: pickStr(o, 'returnTime', 'reportTime'),
    returnTransportType: pickStr(o, 'returnTransportType'),
    returnStayAddress: pickStr(o, 'returnStayAddress', 'entryLocation'),
    returnStayAddressCode: pickStr(o, 'returnStayAddressCode', 'return_stay_address_code'),
    returnContactWay: pickStr(o, 'returnContactWay'),
    returnFlightNo: pickStr(o, 'returnFlightNo'),
    locationLng: pickStr(o, 'locationLng', 'longitude'),
    locationLat: pickStr(o, 'locationLat', 'latitude'),
    reportStatus: parseReportStatus(o.reportStatus ?? o.status),
    rejectReason: pickStr(o, 'rejectReason'),
    auditUser: pickStr(o, 'auditUser'),
    auditTime: pickStr(o, 'auditTime'),
    reportingMethod: parseReportingMethod(o.reportingMethod),
    remark: pickStr(o, 'remark', 'note'),
    createTime: pickStr(o, 'createTime'),
    updateTime: pickStr(o, 'updateTime', 'updatedTime'),
  }
}

function totalFromPage(raw: OverseasReturnPageData): number {
  const t = raw.total ?? raw.totalCount ?? raw.totalElements
  return num(t)
}

function recordsFromPage(raw: OverseasReturnPageData): unknown[] {
  const a = raw.records ?? raw.list ?? raw.rows
  return Array.isArray(a) ? a : []
}

/** 列表页身份证号脱敏：保留前 3 后 4，中间用 * */
export function maskListIdCard(idCard: string | undefined | null): string {
  const s = String(idCard ?? '').trim()
  if (!s) return ''
  if (s.includes('*')) return s
  if (s.length < 8) return s
  return `${s.slice(0, 3)}${'*'.repeat(s.length - 7)}${s.slice(-4)}`
}

/** 列表页手机号脱敏：11 位保留前 3 后 4，其余保留前 2 后 2 */
export function maskListPhone(phone: string | undefined | null): string {
  const s = String(phone ?? '').trim()
  if (!s) return ''
  if (s.includes('*')) return s
  if (/^1\d{10}$/.test(s)) return `${s.slice(0, 3)}****${s.slice(-4)}`
  if (s.length <= 4) return '****'
  return `${s.slice(0, 2)}****${s.slice(-2)}`
}

/**
 * GET /biz/overseas/return/statistics — 归国管理顶部统计
 */
export async function fetchRepatriationOverview(): Promise<RepatriationOverview> {
  const raw = await http.get<OverseasReturnStatisticsPayload>('/biz/overseas/return/statistics')
  const o = raw != null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  return {
    monthlyReturnCount: num(o.monthReturnedCount),
    qrScanCount: num(o.qrCodeScanCount),
    pendingCount: num(o.pendingConfirmCount),
    returnLocationCount: num(o.returnLocationCount),
  }
}

/**
 * GET /biz/overseas/return/page — 归国上报分页列表
 */
export async function fetchRepatriationPage(
  params: RepatriationPageParams,
): Promise<RepatriationPageResult> {
  const q: Record<string, string | number> = {
    current: params.current,
    size: params.size,
  }
  const kw = params.personKeyword?.trim()
  if (kw) q.personKeyword = kw
  if (params.reportStatus) q.reportStatus = params.reportStatus
  if (params.reportingMethod != null) {
    q.reportingMethod = params.reportingMethod
  }
  const loc = params.locationKey?.trim()
  if (loc) q.locationKey = loc
  const code = params.returnStayAddressCode?.trim()
  if (code) q.returnStayAddressCode = code

  const raw = await http.get<OverseasReturnPageData>('/biz/overseas/return/page', { params: q })
  const rows = recordsFromPage(raw)
  const list: RepatriationRecord[] = []
  rows.forEach((item, i) => {
    const row = mapReturnRecord(item, i)
    if (row) list.push(row)
  })
  return {
    list,
    total: totalFromPage(raw),
  }
}

/** 详情接口：原始 data + 供列表/编辑兼容的规范化字段 */
export interface RepatriationDetailResult {
  raw: Record<string, unknown>
  mapped: RepatriationRecord
}

/**
 * GET /biz/overseas/return/detail — 归国上报详情（证件号/手机号为真实明文）
 */
export async function fetchRepatriationDetail(
  id: string,
): Promise<RepatriationDetailResult | null> {
  const raw = await http.get<unknown>('/biz/overseas/return/detail', {
    params: { id },
  })
  if (raw == null || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const mapped = mapReturnRecord(o, 0)
  if (!mapped) return null
  return { raw: o, mapped }
}

/** POST /biz/overseas/return/audit 请求体 */
export interface RepatriationAuditPayload {
  id: string
  /** CONFIRMED-已确认，REJECTED-已驳回 */
  reportStatus: 'CONFIRMED' | 'REJECTED'
  /** 驳回原因（驳回时由业务方校验必填） */
  rejectReason?: string
}

/**
 * POST /biz/overseas/return/audit — 审核归国上报（确认 / 驳回）
 */
export async function auditRepatriationReturn(payload: RepatriationAuditPayload): Promise<void> {
  await http.post<void>('/biz/overseas/return/audit', {
    id: payload.id,
    reportStatus: payload.reportStatus,
    rejectReason: payload.rejectReason ?? '',
  })
}

/**
 * POST /biz/overseas/return/delete — 删除归国记录
 */
export async function deleteRepatriationRecord(id: string): Promise<void> {
  const v = String(id ?? '').trim()
  if (!v) return
  await http.post<void>('/biz/overseas/return/delete', { id: v })
}
